import { ChevronDown, ChevronRight } from "lucide-react";
import { useState, type ReactNode } from "react";

interface CollapsibleSectionProps {
  /** Section title shown in the header. */
  title: string;
  /** Optional icon symbol shown before the title (e.g. ``"▣"``). */
  iconSymbol?: string;
  /**
   * Whether the section is expanded by default. The component is
   * uncontrolled — once mounted the user toggles state freely. Pass
   * the same key to ``key=`` on the parent if you need to reset.
   */
  defaultOpen?: boolean;
  /** Optional right-aligned label shown next to the chevron (e.g. badges). */
  rightSlot?: ReactNode;
  /** Optional short description shown below the title in the header row. */
  description?: string;
  children: ReactNode;
}

/**
 * Click-to-toggle collapsible section used in the Dashboard inspector
 * to group related fields (Identity / SQL Query / Behavior /
 * Structural). The header stays visible when collapsed so the admin
 * always knows what sections exist.
 *
 * Iter 27 — introduced to replace the flat vertical-stack of every
 * field at once. Keeps the inspector scrollable for reports with
 * many KPIs/charts/tables.
 */
export function CollapsibleSection({
  title,
  iconSymbol,
  defaultOpen = true,
  rightSlot,
  description,
  children,
}: CollapsibleSectionProps): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="overflow-hidden rounded-md border border-neutral-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 border-b border-neutral-200 bg-neutral-50 px-3 py-2 text-left text-xs font-semibold text-neutral-600 transition-colors hover:bg-neutral-100"
        aria-expanded={open}
      >
        <div className="flex flex-1 items-center gap-2">
          {open ? (
            <ChevronDown size={12} className="shrink-0 text-neutral-500" />
          ) : (
            <ChevronRight size={12} className="shrink-0 text-neutral-500" />
          )}
          {iconSymbol && (
            <span aria-hidden className="text-neutral-500">
              {iconSymbol}
            </span>
          )}
          <span>{title}</span>
        </div>
        {rightSlot && <div className="shrink-0">{rightSlot}</div>}
      </button>
      {description && open && (
        <p className="border-b border-neutral-100 bg-neutral-50/50 px-3 py-2 text-xs text-neutral-500">
          {description}
        </p>
      )}
      {open && <div className="p-3">{children}</div>}
    </div>
  );
}
